import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { Close } from "./icons";

export function SubscriptionChooser({
  children,
  busy,
  onClose,
}: {
  children: React.ReactNode;
  busy: boolean;
  onClose: () => void;
}) {
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const dismiss = useRef(onClose);
  const pending = useRef(busy);
  dismiss.current = onClose;
  pending.current = busy;

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (!pending.current) dismiss.current();
      }
      if (event.key !== "Tab") return;
      const controls = Array.from(
        dialog.current?.querySelectorAll<HTMLElement>(
          "button:not(:disabled), input:not(:disabled), a[href]",
        ) ?? [],
      );
      event.preventDefault();
      if (!controls.length) {
        dialog.current?.focus();
        return;
      }
      const current = controls.indexOf(document.activeElement as HTMLElement);
      const next =
        current < 0
          ? event.shiftKey
            ? controls.length - 1
            : 0
          : (current + (event.shiftKey ? -1 : 1) + controls.length) % controls.length;
      controls[next].focus();
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = previousOverflow;
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, []);

  useEffect(() => {
    if (
      !busy &&
      (document.activeElement === dialog.current ||
        !dialog.current?.contains(document.activeElement))
    ) {
      close.current?.focus();
    }
  }, [busy]);

  return createPortal(
    <div
      className="subscription-chooser"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="subscription-chooser__dialog"
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy}
        tabIndex={-1}
      >
        <button
          className="subscription-chooser__close"
          ref={close}
          onClick={onClose}
          disabled={busy}
          aria-label="Close subscription chooser"
        >
          <Close />
        </button>
        <h2 id={titleId}>Support Wirder</h2>
        {children}
      </div>
    </div>,
    document.body,
  );
}
